import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router'

import {
  ChatRefusedError,
  StreamDroppedError,
  createSession,
  getSession,
  sendMessage,
  type ChatContext,
} from '../lib/assistant'
import { chatContext, chipLabel, screenFor, type MeasureFocus, type PageContext, type ScreenRoute } from '../lib/assistantRoute'
import { EMPTY_THREAD, REFUSAL_CODES, threadReducer, retryDeadline, type ThreadAction, type RefusalCode, type ThreadNotice, type ThreadState } from '../lib/assistantThread'
import { instantAfter } from '../lib/format'
import { clearInterruptedDraft, keepInterruptedDraft, peekInterruptedDraft } from '../lib/assistantDraft'
import { ProblemError, UnauthorizedError } from '../lib/problem'

/**
 * The assistant, app-wide (agent-1-web W2): one conversation, whether it is
 * shown in the drawer, the phone sheet or on `/assistant`, and the screen it
 * was opened from.
 *
 * Mounted ONLY when `ASSISTANT_ENABLED` (App.tsx), so with the flag off there
 * is no provider, `useAssistant()` is null and nothing renders.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
 *
 * It sends nothing on its own. Opening the drawer, "Ask about this" and moving
 * between screens change only the context the NEXT message carries; a
 * conversation is created on the first send (D4), with that context.
 */
export interface AssistantApi {
  open: boolean
  /** Open the drawer, optionally focused on a measure; focus returns to `trigger` on close. */
  openAssistant: (options?: { measure?: MeasureFocus; trigger?: HTMLElement | null }) => void
  closeAssistant: () => void
  /** The product screen the app is on, or null where the assistant does not appear. */
  screen: ScreenRoute | null
  measure: MeasureFocus | null
  /** Payload words for the comparison/run questions. */
  activityLabel: string | null
  clearMeasure: () => void
  /** The context the next message will carry, and the chip that says it. */
  context: ChatContext | null
  chip: string | null
  /** Pages publish their reading here (`usePublishAssistantPage`). */
  setPage: (page: PageContext | null) => void
  thread: ThreadState
  /** Sticky: the chat routes answered the plain not-found (flag off server-side, C71). */
  absent: boolean
  /** Server waits apply across conversations. */
  wait: { code: RefusalCode; retryAt: number } | null
  /** A refused message's text, handed back to the composer. A new object each time, so a repeat re-applies. */
  /** Words handed back to the composer. `restored`: kept across a sign-in (agent-1.1 S9.1). */
  draftBack: { text: string; restored?: true } | null
  send: (text: string) => void
  /** Resend the dropped turn with its own id: the server replays it or answers 409 (C48). */
  resendDropped: () => void
  /** `error` (internal.error) and the 409 left behind it: read the conversation again (C66). */
  reload: () => void
  loadSession: (id: string) => Promise<void>
  loadEarlier: () => Promise<void>
  newConversation: () => void
  /** Called when a conversation is deleted elsewhere (the list on /assistant). */
  forget: (id: string) => void
  dismissNotice: () => void
  /** Where focus goes when the drawer closes. */
  triggerRef: React.RefObject<HTMLElement | null>
}

const AssistantContext = createContext<AssistantApi | null>(null)

/** The assistant, or null when the build has no assistant (flag off) or no provider. */
export function useAssistant(): AssistantApi | null {
  return useContext(AssistantContext)
}

/**
 * A page tells the drawer what it shows: the recording, its condition and the
 * page's own date words for it. Cleared when the page unmounts. A no-op
 * without a provider, so pages call it unconditionally.
 */
export function usePublishAssistantPage(page: PageContext | null) {
  const setPage = useContext(AssistantContext)?.setPage
  const recordingId = page?.recordingId ?? null
  const condition = page?.condition ?? null
  const dateLabel = page?.dateLabel ?? null
  const pairLabel = page?.pairLabel ?? null
  const activityLabel = page?.activityLabel ?? null
  // A layout effect, so the chip never paints one recording's day under the
  // next one's address (W4/W5 review: /history/a -> /history/b).
  useLayoutEffect(() => {
    if (!setPage) return
    setPage(recordingId === null && condition === null && dateLabel === null && pairLabel === null && activityLabel === null ? null : { recordingId, condition, dateLabel, pairLabel, activityLabel })
    return () => setPage(null)
  }, [setPage, recordingId, condition, dateLabel, pairLabel, activityLabel])
}

function isRefusal(code: string | null): code is RefusalCode {
  return code !== null && (REFUSAL_CODES as readonly string[]).includes(code)
}

/** A failed call, as the thread's notice. */
function noticeFor(err: unknown, dropped: () => ThreadNotice, cancelled: boolean): ThreadNotice | null {
  // Silent only when WE cancelled (unmount, a new conversation, another one
  // opened). Any other AbortError is a timeout -- `request`'s own 15 s -- and
  // the person must hear about it (final review).
  if (err instanceof DOMException && err.name === 'AbortError' && cancelled) return null
  if (err instanceof StreamDroppedError) return dropped()
  if (err instanceof ProblemError) {
    if (err.status === 404 && err.code === 'not_found') return { kind: 'absent' }
    if (err.code === 'chat.session_not_found') return { kind: 'gone' }
    if (err.status === 400) return { kind: 'invalid' }
    if (isRefusal(err.code)) {
      const seconds = err instanceof ChatRefusedError ? err.retryAfterS : null
      return { kind: 'refused', code: err.code, retryAt: seconds === null ? null : instantAfter(seconds, Date.now()) }
    }
  }
  return { kind: 'failed' }
}

export function AssistantProvider({ children, accountId = null, experimentsEnabled = false }: { children: ReactNode; accountId?: string | null; experimentsEnabled?: boolean }) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [measure, setMeasure] = useState<MeasureFocus | null>(null)
  const [page, setPage] = useState<PageContext | null>(null)
  const [thread, setThread] = useState(EMPTY_THREAD)
  const [absent, setAbsent] = useState(false)
  const [wait, setWait] = useState<AssistantApi['wait']>(null)
  const waitRef = useRef(wait)
  // agent-1.1 S9.1: words a sign-in expiry interrupted come back to the composer
  // when the same account signs in again (`lib/assistantDraft.ts`).
  const [draftBack, setDraftBack] = useState<{ text: string; restored?: true } | null>(() => {
    const kept = accountId === null ? null : peekInterruptedDraft(accountId)
    return kept === null ? null : { text: kept, restored: true }
  })
  const triggerRef = useRef<HTMLElement | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Stable handlers read the thread through a ref updated by every dispatch.
  const threadRef = useRef(thread)
  // Update synchronously too: two calls in the same event cannot start two turns.
  const dispatch = useCallback((action: ThreadAction) => {
    threadRef.current = threadReducer(threadRef.current, action)
    setThread(threadRef.current)
  }, [])
  const operation = useRef({})
  const earlierBusy = useRef(false)
  const invalidate = useCallback(() => {
    operation.current = {}
    abortRef.current?.abort()
    earlierBusy.current = false
    return operation.current
  }, [])

  const screen = useMemo(() => screenFor(pathname, experimentsEnabled), [pathname, experimentsEnabled])

  // A measure belongs to the screen it was asked about from: moving on drops
  // it. During render, as AppShell does for its drawer, so the chip never
  // paints a measure from the previous screen.
  const [lastPath, setLastPath] = useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    if (measure) setMeasure(null)
  }

  const context = useMemo(() => (screen ? chatContext(screen, page, measure) : null), [screen, page, measure])
  const activityLabel = page?.activityLabel ?? null
  const chip = useMemo(() => (screen ? chipLabel(screen, page, measure) : null), [screen, page, measure])

  useEffect(() => () => { invalidate() }, [invalidate])

  const openAssistant = useCallback<AssistantApi['openAssistant']>((options) => {
    if (options?.measure) setMeasure(options.measure)
    triggerRef.current = options?.trigger ?? null
    setOpen(true)
  }, [])

  const closeAssistant = useCallback(() => setOpen(false), [])
  const clearMeasure = useCallback(() => setMeasure(null), [])

  // agent-1.1 S9.1: the stored copy of interrupted words is read once (the
  // `draftBack` initialiser) and then forgotten, whoever's it was.
  useEffect(() => {
    clearInterruptedDraft()
  }, [])

  const handleFailure = useCallback((err: unknown, turn: { clientMessageId: string; text: string; context: ChatContext }, resend: boolean, streamed: boolean, cancelled: boolean) => {
    // A 401 signs the person out and unmounts this provider: keep their words
    // for the sign-in, under their account, before the in-memory copy is gone.
    if (err instanceof UnauthorizedError && accountId !== null && !streamed) {
      keepInterruptedDraft(accountId, turn.text)
    }
    const notice = noticeFor(err, () => ({ kind: 'dropped', ...turn }), cancelled)
    if (notice === null) {
      dispatch({ type: 'settled' })
      return
    }
    if (notice.kind === 'refused' && notice.retryAt !== null && notice.retryAt > (waitRef.current?.retryAt ?? 0)) {
      waitRef.current = { code: notice.code, retryAt: notice.retryAt }
      setWait(waitRef.current)
    }
    if (notice.kind === 'absent') setAbsent(true)
    // Never accepted -- refused before its stream began, whatever the reason --
    // so the reducer removes its bubble and the words go back to the composer
    // (W2/W3 review: `gone` and `failed` used to lose them). Not for a resend,
    // whose turn the server may hold, and not once the stream had begun.
    if (!resend && !streamed && notice.kind !== 'dropped' && notice.kind !== 'error') {
      setDraftBack({ text: turn.text })
    }
    dispatch({ type: 'notice', notice })
    // The conversation is gone: the next send starts a new one.
    if (notice.kind === 'gone') dispatch({ type: 'session', session: null })
  }, [dispatch, accountId])

  // A failed re-read is silent: the old title stays.
  const refreshTitle = useCallback(async (id: string, signal: AbortSignal) => {
    try {
      const detail = await getSession(id, { limit: 1 }, signal)
      dispatch({ type: 'renamed', session: detail.session })
    } catch {
      // keep the title the thread holds
    }
  }, [dispatch])

  const run = useCallback(
    async (text: string, clientMessageId: string, turnContext: ChatContext, resend = false) => {
      const token = invalidate()
      const current = () => token === operation.current
      const turn = { clientMessageId, text, context: turnContext }
      dispatch({ type: 'send', turn, now: new Date().toISOString() })
      const controller = new AbortController()
      abortRef.current = controller
      let streamed = false
      let done = false
      // The server names a conversation when its first reply is stored
      // (session-titles): after a conversation's first completed turn, read it
      // again so the new title shows without a reload.
      const firstTurn = !threadRef.current.messages.some((m) => m.role === 'assistant' && m.status === 'complete')
      try {
        let sessionId = threadRef.current.session?.id
        if (!sessionId) {
          const session = await createSession(turnContext, controller.signal)
          if (!current()) return
          dispatch({ type: 'session', session })
          sessionId = session.id
        }
        await sendMessage({
          sessionId,
          text,
          context: turnContext,
          clientMessageId,
          signal: controller.signal,
          onEvent: (frame) => {
            if (!current()) return
            if (frame.event === 'session') streamed = true
            if (frame.event === 'done') done = true
            dispatch({ type: 'event', frame })
          },
        })
        if (current()) dispatch({ type: 'settled' })
        if (current() && done && firstTurn) void refreshTitle(sessionId, controller.signal)
      } catch (err) {
        if (!current()) return
        handleFailure(err, turn, resend, streamed, controller.signal.aborted)
      }
    },
    [handleFailure, invalidate, dispatch, refreshTitle],
  )

  const send = useCallback(
    (text: string) => {
      // Every way of sending -- the composer, a starter, a follow-up, "Try
      // again" -- comes through here, so the waits are enforced here, not only
      // by a disabled button (W2/W3 review).
      if (threadRef.current.loading || threadRef.current.turn || !context || absent) return
      const notice = threadRef.current.notice
      const deadline = retryDeadline(notice)
      if ((deadline !== null && deadline > Date.now()) || (waitRef.current !== null && waitRef.current.retryAt > Date.now())) return
      // Whatever was handed back has now been sent (or replaced): a composer that
      // mounts later must not bring it back (agent-1.1 S9.1).
      setDraftBack(null)
      void run(text, crypto.randomUUID(), context)
    },
    [context, run, absent],
  )

  const resendDropped = useCallback(() => {
    const notice = threadRef.current.notice
    if (notice?.kind !== 'dropped' || threadRef.current.turn || threadRef.current.loading || absent) return
    const deadline = retryDeadline(notice)
    if ((deadline !== null && deadline > Date.now()) || (waitRef.current !== null && waitRef.current.retryAt > Date.now())) return
    void run(notice.text, notice.clientMessageId, notice.context, true)
  }, [run, absent])

  const loadSession = useCallback(async (id: string) => {
    // One read at a time, and none landing on a turn: a later choice wins.
    const token = invalidate()
    dispatch({ type: 'select', id })
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const detail = await getSession(id, {}, controller.signal)
      if (token !== operation.current) return
      dispatch({ type: 'loaded', detail })
    } catch (err) {
      if (token !== operation.current) return
      const notice = noticeFor(err, () => ({ kind: 'failed' }), controller.signal.aborted)
      if (notice?.kind === 'absent') setAbsent(true)
      if (notice) dispatch({ type: 'selectionFailed', notice })
    }
  }, [invalidate, dispatch])

  const reload = useCallback(() => {
    if (threadRef.current.turn || threadRef.current.loading) return
    const id = threadRef.current.session?.id
    if (id) void loadSession(id)
  }, [loadSession])

  const loadEarlier = useCallback(async () => {
    const { session, nextBefore } = threadRef.current
    if (!session || !nextBefore || threadRef.current.turn || threadRef.current.loading || earlierBusy.current) return
    const token = operation.current
    earlierBusy.current = true
    try {
      // The reducer drops a page that belongs to another conversation by then.
      const detail = await getSession(session.id, { before: nextBefore })
      if (token !== operation.current) return
      dispatch({ type: 'earlier', detail })
    } catch (err) {
      if (token !== operation.current) return
      const notice = noticeFor(err, () => ({ kind: 'failed' }), false)
      if (notice) dispatch({ type: 'notice', notice })
    } finally {
      if (token === operation.current) earlierBusy.current = false
    }
  }, [dispatch])

  const newConversation = useCallback(() => {
    invalidate()
    dispatch({ type: 'reset' })
  }, [invalidate, dispatch])

  const forget = useCallback((id: string) => {
    if (threadRef.current.session?.id === id || threadRef.current.loading === id) {
      invalidate()
      dispatch({ type: 'reset' })
    }
  }, [invalidate, dispatch])

  const dismissNotice = useCallback(() => {
    // A dropped turn owns its retry ID; a wait cannot be dismissed into a new POST.
    if (threadRef.current.notice?.kind === 'dropped' || retryDeadline(threadRef.current.notice) !== null) return
    dispatch({ type: 'dismiss' })
  }, [dispatch])

  const api = useMemo<AssistantApi>(
    () => ({
      open,
      openAssistant,
      closeAssistant,
      screen,
      measure,
      activityLabel,
      clearMeasure,
      context,
      chip,
      setPage,
      thread,
      absent,
      wait,
      draftBack,
      send,
      resendDropped,
      reload,
      loadSession,
      loadEarlier,
      newConversation,
      forget,
      dismissNotice,
      triggerRef,
    }),
    [
      open,
      openAssistant,
      closeAssistant,
      screen,
      measure,
      clearMeasure,
      context,
      chip,
      activityLabel,
      thread,
      absent,
      wait,
      draftBack,
      send,
      resendDropped,
      reload,
      loadSession,
      loadEarlier,
      newConversation,
      forget,
      dismissNotice,
    ],
  )

  return <AssistantContext.Provider value={api}>{children}</AssistantContext.Provider>
}
